<template>
  <div class="invoice-list-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>سجل الفواتير المالية</h2>
        </div>
      </template>

      <el-alert
        title="متابعة التحصيل المالي"
        type="success"
        description="هذه الشاشة هي المركز الرئيسي لمتابعة الفواتير. يمكنك البحث عن فاتورة محددة، عرض تفاصيلها، أو تسجيل دفعة جديدة لأي فاتورة غير مسددة بالكامل."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-input
        v-model="searchQuery"
        placeholder="ابحث برقم الفاتورة أو اسم ولي الأمر..."
        @input="handleSearch"
        clearable
        class="search-input"
      >
        <template #prepend><el-icon><Search /></el-icon></template>
      </el-input>

      <el-table :data="invoices" v-loading="loading" border style="width: 100%; margin-top: 20px;">
        <el-table-column prop="invoice_number" label="رقم الفاتورة" width="150" />
        <el-table-column label="ولي الأمر" min-width="180">
            <template #default="{ row }">{{ row.parent.first_name }} {{ row.parent.last_name }}</template>
        </el-table-column>
        <el-table-column label="تاريخ الإصدار" width="120">
    <template #default="{ row }">
        {{ formatDate(row.issue_date) }}
    </template>
</el-table-column>
        <el-table-column prop="final_amount" label="المبلغ النهائي" width="120" />
        <el-table-column prop="paid_amount" label="المبلغ المدفوع" width="120" />
        <el-table-column prop="remaining_amount" label="المبلغ المتبقي" width="120" />
        <el-table-column label="الحالة" width="120">
            <template #default="{ row }">
                <el-tag :type="getStatusTagType(row.status)">{{ row.status }}</el-tag>
            </template>
        </el-table-column>
        <el-table-column label="الإجراءات" width="220" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="openDetailsDialog(row)">عرض التفاصيل</el-button>
            <el-button size="small" type="primary" @click="openPaymentDialog(row)" :disabled="row.status === 'paid'">
                <el-icon><Money /></el-icon> تسجيل دفعة
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-if="pagination.total > 0"
          @current-change="handlePageChange"
          :current-page="pagination.current_page"
          :page-size="pagination.per_page"
          layout="total, prev, pager, next"
          :total="pagination.total"
          background
        />
      </div>
    </el-card>

    <el-dialog v-model="detailsDialogVisible" title="تفاصيل الفاتورة" width="70%">
        <div v-if="selectedInvoice" v-loading="detailsLoading">
            <el-descriptions :column="2" border>
                <el-descriptions-item label="رقم الفاتورة">{{ selectedInvoice.invoice_number }}</el-descriptions-item>
                <el-descriptions-item label="ولي الأمر">{{ selectedInvoice.parent.first_name }} {{ selectedInvoice.parent.last_name }}</el-descriptions-item>
                <el-descriptions-item label="تاريخ الإصدار">{{ formatDate(selectedInvoice.issue_date) }}</el-descriptions-item>
                <el-descriptions-item label="تاريخ الاستحقاق">{{ formatDate(selectedInvoice.due_date) }}</el-descriptions-item>
                <el-descriptions-item label="المبلغ الإجمالي">{{ selectedInvoice.total_amount }} دينار</el-descriptions-item>
                <el-descriptions-item label="الخصم">{{ selectedInvoice.discount }} دينار</el-descriptions-item>
                <el-descriptions-item label="المبلغ النهائي">{{ selectedInvoice.final_amount }} دينار</el-descriptions-item>
                <el-descriptions-item label="الحالة">
                    <el-tag :type="getStatusTagType(selectedInvoice.status)">{{ selectedInvoice.status }}</el-tag>
                </el-descriptions-item>
            </el-descriptions>

            <el-divider>بنود الفاتورة</el-divider>
            <el-table :data="selectedInvoice.items" border>
                <el-table-column prop="student.name" label="الطالب" />
                <el-table-column prop="description" label="البند" />
                <el-table-column prop="amount" label="المبلغ" />
            </el-table>

            <el-divider>الدفعات المسجلة</el-divider>
            <el-table :data="selectedInvoice.payments" border>
                <el-table-column label="تاريخ الدفعة">
        <template #default="{ row }">
            {{ formatDate(row.payment_date) }}
        </template>
    </el-table-column>
                <el-table-column prop="amount" label="المبلغ المدفوع" />
                <el-table-column prop="payment_method" label="طريقة الدفع" />
                <el-table-column prop="treasury.name" label="الخزينة" />
            </el-table>
        </div>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="printInvoice"><el-icon><Printer /></el-icon> طباعة</el-button>
                <el-button @click="detailsDialogVisible = false">إغلاق</el-button>
            </span>
        </template>
    </el-dialog>

    <el-dialog v-model="paymentDialogVisible" title="تسجيل دفعة جديدة" width="40%" :close-on-click-modal="false">
        <el-form v-if="selectedInvoice" :model="paymentForm" ref="paymentFormRef" label-position="top" :rules="paymentRules">
            <p>المبلغ المتبقي على الفاتورة: <strong>{{ selectedInvoice.remaining_amount }} دينار</strong></p>
            <el-form-item label="المبلغ المدفوع" prop="amount_paid">
                <el-input-number v-model="paymentForm.amount_paid" :min="0.01" :max="selectedInvoice.remaining_amount" :precision="2" style="width: 100%;" />
            </el-form-item>
            <el-form-item label="الخزينة" prop="treasury_id">
                <el-select v-model="paymentForm.treasury_id" placeholder="اختر الخزينة" style="width: 100%;">
                    <el-option v-for="treasury in treasuries" :key="treasury.id" :label="treasury.name" :value="treasury.id" />
                </el-select>
            </el-form-item>
             <el-form-item label="تاريخ الدفعة" prop="payment_date">
                <el-date-picker v-model="paymentForm.payment_date" type="date" placeholder="اختر تاريخ الدفعة" style="width: 100%;" />
            </el-form-item>
        </el-form>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="paymentDialogVisible = false">إلغاء</el-button>
                <el-button type="primary" @click="submitPayment" :loading="isSavingPayment">حفظ الدفعة</el-button>
            </span>
        </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Search, Money, Printer } from '@element-plus/icons-vue';
import debounce from 'lodash/debounce';

export default {
  name: 'InvoiceList',
  components: { Search, Money, Printer },
  data() {
    return {
      api: null,
      loading: false,
      invoices: [],
      searchQuery: '',
      pagination: {
        current_page: 1,
        per_page: 15,
        total: 0,
      },
      
      detailsDialogVisible: false,
      detailsLoading: false,
      selectedInvoice: null,
      
      paymentDialogVisible: false,
      isSavingPayment: false,
      paymentForm: this.getInitialPaymentForm(),
      paymentRules: {
        amount_paid: [{ required: true, message: 'المبلغ المدفوع مطلوب', trigger: 'blur' }],
        treasury_id: [{ required: true, message: 'الخزينة مطلوبة', trigger: 'change' }],
        payment_date: [{ required: true, message: 'تاريخ الدفعة مطلوب', trigger: 'change' }],
      },
      treasuries: [],
    };
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchInvoices();
    this.fetchTreasuries();
  },
  methods: {
    getInitialPaymentForm() {
        return {
            invoice_id: null,
            treasury_id: null,
            amount_paid: 0,
            payment_date: new Date(),
            payment_method: 'cash',
        };
    },
    // ... داخل export default { ... methods: { ... } }
formatDate(dateString) {
    if (!dateString) return ''; // إذا كان التاريخ غير موجود، أرجع قيمة فارغة
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // الشهر يبدأ من 0، لذا +1
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
},
// ... باقي الدوال مثل printInvoice, getStatusTagType, etc.
    async fetchInvoices(page = 1) {
      this.loading = true;
      try {
        const response = await this.api.get('/invoices', {
          params: {
            search: this.searchQuery,
            page: page,
            per_page: this.pagination.per_page
          }
        });
        
        if (response && response.data) {
          this.invoices = response.data.data || [];
          this.pagination.total = response.data.total || 0;
          this.pagination.current_page = response.data.current_page || 1;
          this.pagination.per_page = response.data.per_page || 15;
        } else {
            this.invoices = [];
            this.pagination.total = 0;
            ElMessage.error('Received an invalid response from the server.');
        }

      } catch (error) {
        console.error("Error fetching invoices:", error);
        ElMessage.error('فشل في جلب الفواتير.');
      } finally {
        this.loading = false;
      }
    },
    handleSearch: debounce(function() {
        this.fetchInvoices(1);
    }, 500),
    handlePageChange(page) {
      this.fetchInvoices(page);
    },
    async openDetailsDialog(invoice) {
        this.detailsDialogVisible = true;
        this.detailsLoading = true;
        try {
            const response = await this.api.get(`/invoices/${invoice.id}`);
            this.selectedInvoice = response.data;
        } catch (error) {
            ElMessage.error('فشل في جلب تفاصيل الفاتورة.');
            this.detailsDialogVisible = false;
        } finally {
            this.detailsLoading = false;
        }
    },
    openPaymentDialog(invoice) {
      this.selectedInvoice = invoice;
      this.paymentForm = this.getInitialPaymentForm();
      this.paymentForm.invoice_id = invoice.id;
      this.paymentDialogVisible = true;
    },
    async submitPayment() {
        if (!this.$refs.paymentFormRef) return;
        await this.$refs.paymentFormRef.validate(async (valid) => {
            if (!valid) return;
            this.isSavingPayment = true;
            try {
                await this.api.post('/payments', this.paymentForm);
                ElMessage.success('تم تسجيل الدفعة بنجاح!');
                this.paymentDialogVisible = false;
                this.fetchInvoices(this.pagination.current_page);
            } catch (error) {
                ElMessage.error(error.response?.data?.message || 'فشل في تسجيل الدفعة.');
            } finally {
                this.isSavingPayment = false;
            }
        });
    },
    async fetchTreasuries() {
      try {
        const response = await this.api.get('/treasuries');
        this.treasuries = response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب الخزائن.');
      }
    },
    getStatusTagType(status) {
      const statusMap = {
        paid: 'success',
        partially_paid: 'warning',
        overdue: 'danger',
        unpaid: 'info',
      };
      return statusMap[status] || 'info';
    },
    printInvoice() {
    if (!this.selectedInvoice) return;

    const logoUrl = `${window.location.origin}/logo-school-one.png`;
    const invoice = this.selectedInvoice;
    
    // Generates HTML for each invoice item
    const itemsHtml = invoice.items.map(item => `
        <tr>
            <td>${item.student.name}</td>
            <td>${item.description}</td>
            <td class="text-right">${parseFloat(item.amount).toFixed(2)}</td>
        </tr>
    `).join('');

    // Generates HTML for each payment record, now using the date formatter
    const paymentsHtml = invoice.payments.map(payment => `
        <tr>
            <td>${this.formatDate(payment.payment_date)}</td>
            <td class="text-right">${parseFloat(payment.amount).toFixed(2)}</td>
        </tr>
    `).join('');
    
    const totalPaid = invoice.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
    const remainingAmount = parseFloat(invoice.final_amount) - totalPaid;

    // The main HTML content for the invoice
    const printContent = `
        <html>
        <head>
            <title>Invoice #${invoice.invoice_number}</title>
            <style>
                /* Using a common web-safe font for English */
                @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
                body { 
                    font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; 
                    direction: ltr; /* Set direction to Left-to-Right */
                    margin: 0;
                    padding: 0;
                }
                .invoice-container {
                    position: relative;
                    max-width: 800px; 
                    margin: auto; 
                    padding: 30px; 
                    border: 1px solid #eee; 
                    box-shadow: 0 0 10px rgba(0, 0, 0, 0.15); 
                    font-size: 16px; 
                    line-height: 24px; 
                    color: #333;
                }
                /* Watermark styles */
                .invoice-container::after {
                    content: '';
                    background-image: url(${logoUrl});
                    background-repeat: no-repeat;
                    background-position: center;
                    background-size: 300px;
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    opacity: 0.08;
                    z-index: -1;
                }
                .invoice-box table { 
                    width: 100%; 
                    line-height: inherit; 
                    text-align: left; /* Default text alignment to left */
                    border-collapse: collapse;
                }
                .invoice-box table td { padding: 8px; vertical-align: top; }
                .invoice-box .header-table td { padding-bottom: 20px; }
                .invoice-box .header-table .title img { width:100%; max-width:150px; }
                .invoice-box .header-table .invoice-details { text-align: right; } /* Align invoice details to the right */
                .invoice-box .info-table td { padding-bottom: 40px; }
                .invoice-box table tr.heading td { 
                    background: #f2f2f2; 
                    border-bottom: 1px solid #ddd; 
                    font-weight: bold; 
                }
                .invoice-box table tr.item td { border-bottom: 1px solid #eee; }
                .invoice-box table tr.total td { font-weight: bold; border-top: 2px solid #eee; }
                .summary-table { margin-top: 20px; width: 50%; float: right; } /* Float summary to the right */
                .summary-table td:nth-child(2) { text-align: right; }
                .text-right { text-align: right; } /* Utility class for right alignment */
                .signature-area {
                    margin-top: 100px;
                    padding-top: 20px;
                    width: 100%;
                    display: block;
                    clear: both;
                }
                .signature-box {
                   float: right; /* Float signature to the right */
                   width: 250px;
                   text-align: center;
                }
                .signature-box .line {
                    border-top: 1px solid #555;
                    margin: 40px 0 5px 0;
                }
                @media print {
                    body, .invoice-box { 
                        -webkit-print-color-adjust: exact; 
                        print-color-adjust: exact; 
                        box-shadow: none;
                        border: none;
                    }
                }
            </style>
        </head>
        <body>
            <div class="invoice-container">
                <div class="invoice-box">
                    <table class="header-table" cellpadding="0" cellspacing="0">
                        <tr>
                            <td class="title"><img src="${logoUrl}" alt="Company Logo"></td>
                            <td class="invoice-details">
                                <h3>Financial Invoice</h3>
                                <b>Invoice No:</b> ${invoice.invoice_number}<br>
                                <b>Issue Date:</b> ${this.formatDate(invoice.issue_date)}<br>
                                <b>Due Date:</b> ${this.formatDate(invoice.due_date)}
                            </td>
                        </tr>
                    </table>
                    <table class="info-table" cellpadding="0" cellspacing="0">
                         <tr>
                            <td>
                                <strong>Bill To:</strong><br>
                                ${invoice.parent.first_name} ${invoice.parent.last_name}<br>
                                ${invoice.parent.email || ''}
                            </td>
                        </tr>
                    </table>
                    
                    <h4 style="margin-bottom: 10px;">Invoice Items</h4>
                    <table cellpadding="0" cellspacing="0">
                        <tr class="heading">
                            <td>Student</td>
                            <td>Description</td>
                            <td class="text-right">Amount (LYD)</td>
                        </tr>
                        ${itemsHtml}
                    </table>
                    
                    <h4 style="margin-top: 30px; margin-bottom: 10px;">Registered Payments</h4>
                    <table cellpadding="0" cellspacing="0">
                        <tr class="heading">
                            <td>Payment Date</td>
                            <td class="text-right">Amount (LYD)</td>
                        </tr>
                        ${paymentsHtml}
                    </table>

                    <table class="summary-table">
                        <tr>
                            <td>Subtotal:</td>
                            <td>${parseFloat(invoice.total_amount).toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td>Discount:</td>
                            <td>${parseFloat(invoice.discount).toFixed(2)}</td>
                        </tr>
                        <tr class="total">
                            <td>Total Amount:</td>
                            <td>${parseFloat(invoice.final_amount).toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td>Total Paid:</td>
                            <td>${totalPaid.toFixed(2)}</td>
                        </tr>
                        <tr class="total">
                            <td>Amount Due:</td>
                            <td>${remainingAmount.toFixed(2)}</td>
                        </tr>
                    </table>

                    <div class="signature-area">
                        <div class="signature-box">
                            <b>Seal & Signature</b>
                            <div class="line"></div>
                            <span>Finance Department</span>
                        </div>
                    </div>

                </div>
            </div>
        </body>
        </html>
    `;

    // Opens a new window and writes the HTML content to it for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
        printWindow.print();
        printWindow.close();
    }, 250);
},
  }
};
</script>

<style scoped>
.invoice-list-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.search-input { max-width: 400px; }
.pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}
</style>